import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Globe, Maximize, Minimize } from "lucide-react";
import { Button } from "@/components/ui/button";

import { API } from "./guest/utils";
import { stopAllAudio, playVoiceMessage } from "./guest/voiceUtils";
import { GuestPortalProvider, useGuestPortal } from "./guest/GuestPortalContext";

// Guest Portal sub-components
import { MainMenu } from "./guest/MainMenu";
import { RegisterForm } from "./guest/RegisterForm";
import { CheckInWrapper } from "./guest/CheckInWrapper";
import { BitsyCheckinView } from "./guest/BitsyCheckinView";
import { CheckOutWrapper } from "./guest/CheckOutWrapper";
import { SignInSheetView } from "./guest/SignInSheetView";
import { HelpView } from "./guest/HelpView";
import { SuccessScreen } from "./guest/SuccessScreen";
import { OfflineBanner } from "./guest/OfflineBanner";

export default function GuestPortal() {
  return (
    <GuestPortalProvider>
      <GuestPortalInner />
    </GuestPortalProvider>
  );
}

function GuestPortalInner() {
  const { language, toggleLanguage, t } = useGuestPortal();

  const [view, setView] = useState("menu");
  const [successMessage, setSuccessMessage] = useState({ title: "", message: "", subMessage: "" });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [bitsyCrewEnabled, setBitsyCrewEnabled] = useState(false);

  // Active Notice State
  const [activeNotice, setActiveNotice] = useState(null);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [noticeAccepted, setNoticeAccepted] = useState(false);
  const [acceptingNotice, setAcceptingNotice] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [noticeTelegram, setNoticeTelegram] = useState('');
  const [acceptedNoticeId, setAcceptedNoticeId] = useState(null);

  useEffect(() => {
    const checkActiveNotice = async () => {
      try {
        const res = await axios.get(`${API}/active-notice`);
        if (res.data) { setActiveNotice(res.data); setNoticeAccepted(false); }
        else { setActiveNotice(null); setNoticeAccepted(true); }
      } catch (err) { setActiveNotice(null); setNoticeAccepted(true); }
    };
    checkActiveNotice();
    const interval = setInterval(checkActiveNotice, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchBitsyCrewSetting = async () => {
      try {
        const res = await axios.get(`${API}/guest-portal/settings`);
        setBitsyCrewEnabled(res.data.bitsy_crew_enabled === true);
      } catch (err) { setBitsyCrewEnabled(false); }
    };
    fetchBitsyCrewSetting();
  }, []);

  const playCustomVoiceMessage = async (text) => {
    if (!text) return;
    try {
      const res = await axios.get(`${API}/voice-dynamic/notice/guest?lang=${language === 'es' ? 'es' : 'en'}&custom_text=${encodeURIComponent(text)}`, { responseType: 'blob' });
      const audio = new Audio(URL.createObjectURL(res.data));
      audio.play();
    } catch (err) { console.log("Voice message not available"); }
  };

  const handleViewChange = (newView) => {
    if (activeNotice && !noticeAccepted && newView === 'checkin') {
      setPendingAction(newView);
      setShowNoticeModal(true);
      if (activeNotice.voice_enabled) {
        let voiceText = activeNotice.voice_message || `Attention: ${activeNotice.title}. ${activeNotice.message}.`;
        if (activeNotice.estimated_duration) voiceText += ` Estimated duration: ${activeNotice.estimated_duration}.`;
        else if (activeNotice.estimated_end_time) voiceText += ` Expected to be resolved by ${new Date(activeNotice.estimated_end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}.`;
        voiceText += activeNotice.notice_type !== 'info' ? ' You must acknowledge this notice to continue.' : ' Press continue to proceed with check-in.';
        playCustomVoiceMessage(voiceText);
      }
    } else {
      if (newView === 'checkin') playVoiceMessage("enter_employee_number");
      if (newView === 'checkout') playVoiceMessage("checkout_enter_room");
      setView(newView);
    }
  };

  const handleAcceptNotice = async (guestName = '', employeeNumber = '', company = '', telegramUsername = '') => {
    if (!activeNotice) return;
    setAcceptingNotice(true);
    try {
      const response = await axios.post(`${API}/notice-acceptance`, {
        notice_id: activeNotice.id, guest_name: guestName || 'Pending Check-in',
        employee_number: employeeNumber, company, telegram_username: telegramUsername || null
      });
      if (response.data?.id) setAcceptedNoticeId(response.data.id);
      const acceptedNotices = JSON.parse(localStorage.getItem('acceptedNotices') || '{}');
      acceptedNotices[activeNotice.id] = new Date().toISOString();
      localStorage.setItem('acceptedNotices', JSON.stringify(acceptedNotices));
      setNoticeAccepted(true); setShowNoticeModal(false); setNoticeTelegram('');
      if (pendingAction) { setView(pendingAction); setPendingAction(null); }
    } catch (err) { toast.error("Failed to record acceptance. Please try again."); }
    finally { setAcceptingNotice(false); }
  };

  useEffect(() => { if (view === 'menu') stopAllAudio(); }, [view]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    else document.exitFullscreen().then(() => setIsFullscreen(false));
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    let idleTimer, refreshInterval;
    const resetToMenu = () => { if (view !== "menu") setView("menu"); };
    const resetIdleTimer = () => { clearTimeout(idleTimer); idleTimer = setTimeout(resetToMenu, 2 * 60 * 1000); };
    refreshInterval = setInterval(() => { if (view === "menu") window.location.reload(); }, 10 * 60 * 1000);
    let wakeLock = null;
    (async () => { try { if ('wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen'); } catch (e) {} })();
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer));
    resetIdleTimer();
    return () => { clearTimeout(idleTimer); clearInterval(refreshInterval); events.forEach(e => document.removeEventListener(e, resetIdleTimer)); if (wakeLock) wakeLock.release(); };
  }, [view]);

  return (
    <div className="kiosk-container grid-bg min-h-screen relative overflow-y-auto">
      <OfflineBanner />
      <div className="absolute top-3 sm:top-6 left-3 sm:left-6 z-10">
        <img src="/hodler-logo.png" alt="Hodler Inn" className="h-10 sm:h-14 object-contain" />
      </div>

      <div className="absolute top-3 sm:top-6 right-3 sm:right-6 flex items-center gap-2 z-10">
        <button onClick={toggleLanguage} className="flex items-center gap-1 sm:gap-2 text-vault-text-secondary hover:text-vault-gold transition-colors bg-vault-surface px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-gray-300 shadow-sm" data-testid="language-toggle-btn" title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}>
          <Globe className="w-4 h-4" />
          <span className="text-xs sm:text-sm font-mono font-bold">{language === 'en' ? 'EN' : 'ES'}</span>
        </button>
        <button onClick={toggleFullscreen} className="flex items-center gap-1 sm:gap-2 text-vault-text-secondary hover:text-vault-gold transition-colors bg-vault-surface px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-gray-300 shadow-sm" data-testid="fullscreen-btn" title={isFullscreen ? "Exit Kiosk Mode" : "Enter Kiosk Mode"}>
          {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          <span className="text-xs sm:text-sm font-mono hidden sm:inline">{isFullscreen ? "Exit Kiosk" : "Kiosk Mode"}</span>
        </button>
      </div>

      <AnimatePresence mode="wait">
        {view === "menu" && (
          <MainMenu key="menu" setView={handleViewChange} activeNotice={activeNotice} noticeAccepted={noticeAccepted} />
        )}
        {view === "register" && (
          <RegisterForm key="register" setView={setView} />
        )}
        {view === "checkin" && (
          <CheckInWrapper key="checkin" setView={setView} setSuccessMessage={setSuccessMessage} setNoticeAccepted={setNoticeAccepted} acceptedNoticeId={acceptedNoticeId} setAcceptedNoticeId={setAcceptedNoticeId} bitsyCrewEnabled={bitsyCrewEnabled} />
        )}
        {view === "bitsy-checkin" && (
          <BitsyCheckinView key="bitsy-checkin" setView={setView} setSuccessMessage={setSuccessMessage} />
        )}
        {view === "checkout" && (
          <CheckOutWrapper key="checkout" setView={setView} setSuccessMessage={setSuccessMessage} bitsyCrewEnabled={bitsyCrewEnabled} />
        )}
        {view === "signin" && (
          <SignInSheetView key="signin" setView={setView} />
        )}
        {view === "help" && (
          <HelpView key="help" setView={setView} />
        )}
        {view === "checkin-success" && (
          <SuccessScreen key="checkin-success" setView={setView} successMessage={successMessage} />
        )}
        {view === "checkout-success" && (
          <SuccessScreen key="checkout-success" setView={setView} successMessage={successMessage} />
        )}
      </AnimatePresence>

      {/* Active Notice Modal */}
      {showNoticeModal && activeNotice && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl ${activeNotice.notice_type === 'critical' ? 'bg-red-900 border-4 border-red-500' : activeNotice.notice_type === 'warning' ? 'bg-amber-900 border-4 border-amber-500' : 'bg-blue-900 border-4 border-blue-500'}`}>
            <div className={`p-6 text-center ${activeNotice.notice_type === 'critical' ? 'bg-red-800' : activeNotice.notice_type === 'warning' ? 'bg-amber-800' : 'bg-blue-800'}`}>
              <div className="flex justify-center mb-4">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center animate-pulse ${activeNotice.notice_type === 'critical' ? 'bg-red-600' : activeNotice.notice_type === 'warning' ? 'bg-amber-600' : 'bg-blue-600'}`}>
                  <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                </div>
              </div>
              <h2 className="text-3xl font-bold text-white uppercase tracking-wider">NOTICE</h2>
              <h3 className="text-2xl font-bold text-white mt-2">{activeNotice.title}</h3>
            </div>
            <div className="p-8 text-center">
              <p className="text-xl text-white leading-relaxed mb-4">{activeNotice.message}</p>
              {(activeNotice.estimated_duration || activeNotice.estimated_end_time) && (
                <div className="bg-white/10 rounded-lg p-4 mb-6 inline-block">
                  <div className="flex items-center gap-2 justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="text-white font-semibold text-lg">{language === 'es' ? 'Tiempo estimado: ' : 'Estimated time: '}{activeNotice.estimated_duration || `Until ${new Date(activeNotice.estimated_end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}</span>
                  </div>
                </div>
              )}
              {activeNotice.notice_type !== 'info' ? (
                <>
                  <div className="bg-black/30 rounded-lg p-4 mb-4">
                    <p className="text-white text-lg">{language === 'es' ? '¿Acepta estas condiciones para continuar con el registro?' : 'Do you accept these conditions to continue with check-in?'}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 mb-6">
                    <label className="text-white font-medium text-sm block mb-2">{language === 'es' ? '📱 ¿Recibir actualizaciones por Telegram?' : '📱 Get notified when resolved via Telegram?'}</label>
                    <input type="text" value={noticeTelegram} onChange={(e) => setNoticeTelegram(e.target.value)} placeholder={language === 'es' ? '@su_nombre_de_usuario' : '@your_username'} className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 text-center text-lg" data-testid="notice-telegram-input" />
                    <p className="text-amber-300/80 text-xs mt-3 italic">{language === 'es' ? '💡 Opcional' : '💡 Optional'}</p>
                  </div>
                  <Button onClick={() => handleAcceptNotice('', '', '', noticeTelegram)} disabled={acceptingNotice} className={`w-full h-16 text-xl font-bold uppercase tracking-wider ${activeNotice.notice_type === 'critical' ? 'bg-red-600 hover:bg-red-500' : 'bg-amber-600 hover:bg-amber-500'} text-white`} data-testid="accept-notice-btn">
                    {acceptingNotice ? (language === 'es' ? 'Procesando...' : 'Processing...') : (language === 'es' ? 'ACEPTO Y CONTINUAR' : 'ACCEPT AND CONTINUE')}
                  </Button>
                  <button onClick={() => { setShowNoticeModal(false); setPendingAction(null); setNoticeTelegram(''); }} className="mt-4 text-white/70 hover:text-white underline text-sm">{language === 'es' ? '← Volver al menú' : '← Back to menu'}</button>
                </>
              ) : (
                <>
                  <Button onClick={() => { setShowNoticeModal(false); setNoticeAccepted(true); if (pendingAction) { setView(pendingAction); setPendingAction(null); } }} className="w-full h-16 text-xl font-bold uppercase tracking-wider bg-blue-600 hover:bg-blue-500 text-white" data-testid="continue-notice-btn">
                    {language === 'es' ? 'CONTINUAR' : 'CONTINUE'}
                  </Button>
                  <button onClick={() => { setShowNoticeModal(false); setPendingAction(null); }} className="mt-4 text-white/70 hover:text-white underline text-sm">{language === 'es' ? '← Volver al menú' : '← Back to menu'}</button>
                </>
              )}
            </div>
            {activeNotice.notice_type !== 'info' && (
              <div className="bg-black/30 px-6 py-3 text-center">
                <p className="text-white/60 text-sm">{language === 'es' ? 'Su aceptación será registrada' : 'Your acceptance will be logged'}</p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
